/**
 * SEO score computation — client-side heuristics over the current
 * article draft. Returns 0-100 + the per-check breakdown so the panel
 * can render a checklist.
 */

export type CheckStatus = 'ok' | 'warn' | 'fail';

export interface SeoCheck {
  key: string;
  label: string;
  status: CheckStatus;
  weight: number;
  detail?: string;
}

export interface SeoScoreInput {
  title: string;
  excerpt: string;
  contentHtml: string;
  featuredMediaId: number | null;
  slug?: string | null;
  keyword?: string | null;
}

export interface SeoScoreResult {
  score: number; // 0-100, ceil
  applicableMax: number;
  achieved: number;
  checks: SeoCheck[];
}

function stripTags(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function countWords(html: string): number {
  const text = stripTags(html);
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function hasMatch(html: string, pattern: RegExp): boolean {
  return pattern.test(html);
}

function countMatches(html: string, pattern: RegExp): number {
  return (html.match(pattern) ?? []).length;
}

function firstParagraphText(html: string): string {
  const m = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  return m ? stripTags(m[1]) : stripTags(html).slice(0, 400);
}

function range(value: number, min: number, max: number): CheckStatus {
  if (value >= min && value <= max) return 'ok';
  if (value >= min * 0.6 && value <= max * 1.4) return 'warn';
  return 'fail';
}

export function computeSeoScore(input: SeoScoreInput): SeoScoreResult {
  const checks: SeoCheck[] = [];
  const html = input.contentHtml || '';
  const wordCount = countWords(html);
  const titleLen = input.title.trim().length;
  const excerptLen = input.excerpt.trim().length;

  // 1) Title length 30-65
  checks.push({
    key: 'title-length',
    label: 'Título 30-65 caracteres',
    status: range(titleLen, 30, 65),
    weight: 10,
    detail: `${titleLen} chars`,
  });

  // 2) Excerpt 120-180
  checks.push({
    key: 'excerpt-length',
    label: 'Excerpt 120-180 caracteres',
    status: excerptLen === 0 ? 'fail' : range(excerptLen, 120, 180),
    weight: 10,
    detail: excerptLen === 0 ? 'vazio' : `${excerptLen} chars`,
  });

  // 3) H2 present
  const hasH2 = hasMatch(html, /<h2[\s>]/i);
  checks.push({
    key: 'h2',
    label: 'H2 presente',
    status: hasH2 ? 'ok' : 'fail',
    weight: 10,
  });

  // 4) H3 present
  const hasH3 = hasMatch(html, /<h3[\s>]/i);
  checks.push({
    key: 'h3',
    label: 'H3 presente',
    status: hasH3 ? 'ok' : 'warn',
    weight: 5,
  });

  // 5) Featured image
  checks.push({
    key: 'featured-image',
    label: 'Imagem destacada',
    status: input.featuredMediaId ? 'ok' : 'fail',
    weight: 10,
  });

  // 6) All inline images have alt
  const allImages = countMatches(html, /<img[^>]*>/gi);
  const imagesWithAlt = countMatches(html, /<img[^>]*alt="[^"]+"/gi);
  let altStatus: CheckStatus = 'ok';
  let altDetail = 'sem imagens inline';
  if (allImages > 0) {
    if (imagesWithAlt === allImages) {
      altStatus = 'ok';
      altDetail = `${allImages}/${allImages} com alt`;
    } else {
      altStatus = imagesWithAlt > 0 ? 'warn' : 'fail';
      altDetail = `${imagesWithAlt}/${allImages} com alt`;
    }
  }
  checks.push({
    key: 'img-alt',
    label: 'Alt-text em imagens',
    status: altStatus,
    weight: 10,
    detail: altDetail,
  });

  // 7) Word count 600-2000
  checks.push({
    key: 'word-count',
    label: 'Word count 600-2000',
    status: wordCount === 0 ? 'fail' : range(wordCount, 600, 2000),
    weight: 10,
    detail: `${wordCount} palavras`,
  });

  // 8) Internal links
  const internalLinks = countMatches(html, /<a[^>]*href="\/[^"]*"/gi);
  checks.push({
    key: 'internal-links',
    label: 'Internal links',
    status: internalLinks > 0 ? 'ok' : 'warn',
    weight: 5,
    detail: `${internalLinks} link${internalLinks === 1 ? '' : 's'}`,
  });

  // 9) External links
  const externalLinks = countMatches(html, /<a[^>]*href="https?:[^"]*"/gi);
  checks.push({
    key: 'external-links',
    label: 'External links',
    status: externalLinks > 0 ? 'ok' : 'warn',
    weight: 5,
    detail: `${externalLinks} link${externalLinks === 1 ? '' : 's'}`,
  });

  // 10) URL slug short
  if (input.slug !== undefined) {
    const slug = input.slug ?? '';
    const slugLen = slug.length;
    const status: CheckStatus =
      slugLen > 0 && slugLen <= 75 ? 'ok' : slugLen > 75 ? 'warn' : 'fail';
    checks.push({
      key: 'slug',
      label: 'URL slug curto (<=75)',
      status,
      weight: 5,
      detail: slug ? `${slugLen} chars` : 'vazio',
    });
  }

  // ─── Keyword checks (only when keyword is set) ────────────────────────
  const keyword = (input.keyword ?? '').trim().toLowerCase();
  if (keyword.length >= 2) {
    const titleLower = input.title.toLowerCase();
    checks.push({
      key: 'keyword-title',
      label: 'Keyword no título',
      status: titleLower.includes(keyword) ? 'ok' : 'fail',
      weight: 5,
    });

    const firstPara = firstParagraphText(html).toLowerCase();
    checks.push({
      key: 'keyword-first-paragraph',
      label: 'Keyword no 1.º parágrafo',
      status: firstPara.includes(keyword) ? 'ok' : 'warn',
      weight: 5,
    });

    const text = stripTags(html).toLowerCase();
    const escaped = keyword.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const re = new RegExp(`\\b${escaped}\\b`, 'g');
    const hits = (text.match(re) ?? []).length;
    const density = wordCount > 0 ? (hits / wordCount) * 100 : 0;
    let densityStatus: CheckStatus = 'fail';
    if (density >= 0.5 && density <= 3) densityStatus = 'ok';
    else if (density >= 0.2 && density <= 4) densityStatus = 'warn';
    checks.push({
      key: 'keyword-density',
      label: 'Density 0.5-3%',
      status: densityStatus,
      weight: 10,
      detail: `${density.toFixed(2)}% (${hits}x)`,
    });
  }

  // ─── Aggregate ────────────────────────────────────────────────────────
  const applicableMax = checks.reduce((acc, c) => acc + c.weight, 0);
  const achieved = checks.reduce((acc, c) => {
    if (c.status === 'ok') return acc + c.weight;
    if (c.status === 'warn') return acc + c.weight * 0.5;
    return acc;
  }, 0);
  const score = applicableMax > 0 ? Math.round((achieved / applicableMax) * 100) : 0;

  return { score, applicableMax, achieved, checks };
}
